import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { getSettings, updateSettings, uploadLogo, deleteLogo } from '../services/settingsApi';
import { SETTINGS_TABS } from '../constants/settings';
import { SettingsTabs } from '../components/settings';
import { TabReseauxSociaux, TabStockImport, TabCompany, TabVitrine } from '../components/settings/tabs';

const LEGACY_TAB_REDIRECTS = { templates: 'templates', ai: 'ai' };

// Floating save status bar
const SaveStatusBar = ({ status }) => {
  const isVisible = status === 'pending' || status === 'saving' || status === 'saved' || status === 'error';

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-all duration-300 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
    >
      <div
        className={`pointer-events-auto mb-6 px-5 py-3 rounded-xl shadow-lg border backdrop-blur-sm flex items-center gap-2.5 text-sm font-medium transition-colors duration-300 ${
          status === 'error'
            ? 'bg-red-500/15 border-red-500/30 text-red-400'
            : status === 'saved'
            ? 'bg-green-500/15 border-green-500/30 text-green-400'
            : 'bg-dark-card/95 border-dark-border text-label'
        }`}
      >
        {(status === 'pending' || status === 'saving') && (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-carmin" />
            Enregistrement...
          </>
        )}
        {status === 'saved' && (
          <>
            <CheckCircle className="h-4 w-4" />
            Modifications enregistrées
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="h-4 w-4" />
            Erreur lors de la sauvegarde
          </>
        )}
      </div>
    </div>
  );
};

const Settings = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'social';
  const subtabParam = searchParams.get('subtab') || null;
  // Legacy: ?tab=templates or ?tab=ai → redirect to social with matching sub-tab
  const legacySubTab = LEGACY_TAB_REDIRECTS[tabParam];
  const initialTab = legacySubTab ? 'social' : tabParam;
  const initialSubTab = legacySubTab || subtabParam;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | pending | saving | saved | error
  const initialLoadDone = useRef(false);
  const saveTimeout = useRef(null);
  const savedTimeout = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  // Auto-save settings with debounce
  useEffect(() => {
    if (!initialLoadDone.current || !settings) return;

    // Company tab fields should auto-save (except logo which uses dedicated endpoints)

    // Clear any previous "saved" dismiss timer
    if (savedTimeout.current) clearTimeout(savedTimeout.current);
    setSaveStatus('pending');

    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        await updateSettings(settings);
        setSaveStatus('saved');
        savedTimeout.current = setTimeout(() => setSaveStatus('idle'), 2500);
      } catch (err) {
        setSaveStatus('error');
        savedTimeout.current = setTimeout(() => setSaveStatus('idle'), 4000);
      }
    }, 800);

    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
    };
  }, [settings, activeTab]);

  const loadSettings = async () => {
    try {
      const data = await getSettings();
      // Legacy flat color defaults (for old settings format)
      setSettings({
        ...data,
        story_bg_color: data.story_bg_color || '#1a1a2e',
        story_make_color: data.story_make_color || '#ffffff',
        story_model_color: data.story_model_color || '#ffffff',
        story_accent_color: data.story_accent_color || '#4ade80',
        story_catchphrase_color: data.story_catchphrase_color || '#cccccc',
        story_info_color: data.story_info_color || '#999999',
      });
      // Mark initial load as done after a tick so the useEffect doesn't trigger on first set
      setTimeout(() => { initialLoadDone.current = true; }, 100);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement des paramètres' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    setMessage({ type: '', text: '' });

    try {
      const data = await uploadLogo(file);
      initialLoadDone.current = false;
      setSettings(data);
      setTimeout(() => { initialLoadDone.current = true; }, 100);
      setMessage({ type: 'success', text: 'Logo uploadé' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur lors de l\'upload' });
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Supprimer le logo ?')) return;

    try {
      const data = await deleteLogo();
      initialLoadDone.current = false;
      setSettings(data);
      setTimeout(() => { initialLoadDone.current = true; }, 100);
      setMessage({ type: 'success', text: 'Logo supprimé' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams(tabId !== 'social' ? { tab: tabId } : {});
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'social':
        return <TabReseauxSociaux settings={settings} onChange={setSettings} initialSubTab={initialSubTab} />;
      case 'stock_import':
        return <TabStockImport settings={settings} onChange={setSettings} />;
      case 'vitrine':
        return <TabVitrine settings={settings} onChange={setSettings} />;
      case 'company':
        return (
          <TabCompany
            user={user}
            settings={settings}
            onChange={setSettings}
            onLogoUpload={handleLogoUpload}
            onLogoDelete={handleLogoDelete}
            uploadingLogo={uploadingLogo}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-carmin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-12">
      {/* Header */}
      <div className="mb-6 sticky top-0 bg-dark-bg py-4 z-10">
        <h1 className="text-2xl font-bold text-primary">Paramètres</h1>
      </div>

      {/* Message (logo upload, load errors, etc.) */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-md flex items-center ${
          message.type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5 mr-2" />
          ) : (
            <AlertCircle className="h-5 w-5 mr-2" />
          )}
          {message.text}
        </div>
      )}

      {/* Tabs Navigation */}
      <SettingsTabs
        tabs={SETTINGS_TABS}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Tab Content */}
      {renderTabContent()}

      {/* Floating save status bar */}
      <SaveStatusBar status={saveStatus} />
    </div>
  );
};

export default Settings;
